import { orderSourceLabel } from '@/utils/orderSource';
/**
 * MobileCameraSheet — bottom-sheet triggered when the designer taps a photo
 * tile in MobileDesignList. Fires the OS camera via a hidden file input,
 * uploads through the shared Design photo workflow. Closing this presentation
 * does not cancel a submitted request or close a subsequently opened sheet.
 */
import React, { useRef } from 'react';
import { Camera, Check, Loader2, X } from 'lucide-react';
import { useArrangementPhoto } from '@/hooks/useArrangementPhoto';
import ArrangementPhotoStatus from '@/components/ArrangementPhotoStatus';

export default function MobileCameraSheet({ order, onClose }) {
  const inputRef = useRef(null);
  const workflow = useArrangementPhoto(order?.id, order?.arrangement_photo, {
    assignedDesignerId: order?.assigned_designer_id,
  });
  const { photo, uploading, busy, canUpload, hasSelection, upload, discardSelection } = workflow;

  const hasPhoto = !!photo;
  const num = String(order?.internal_order_number || '').match(/-(\d+)$/);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    // The workflow retains the file, not the input DOM node. Clear immediately
    // so a delayed result cannot clear a different order's newly selected file.
    e.target.value = '';
    if (!file || !canUpload || busy) return;
    void upload(file);
  };

  const handleClose = () => {
    if ((hasSelection || busy) && !window.confirm(busy
      ? 'Close this sheet and discard the selected file for retry? A submitted request may still complete. You can reopen this order to check its status.'
      : 'Close this sheet and discard the selected file? You will need to select it again to upload.')) return;
    discardSelection();
    onClose();
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/40" onClick={handleClose} />

      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl px-5 pt-4 pb-10"
        data-testid="mobile-camera-sheet"
      >
        <div className="w-10 h-1 bg-stone-200 rounded-full mx-auto mb-4" />

        <div className="flex items-start justify-between mb-5">
          <div>
            <p className="text-base font-bold text-stone-900">
              {order?.recipient_name || 'Order'}
            </p>
            <p className="text-xs text-stone-400 mt-0.5">
              {num && <span className="mr-1.5">#{num[1]}</span>}
              {order?.product_description || orderSourceLabel(order, 'Manual')}
            </p>
          </div>
          <button
            onClick={handleClose}
            aria-label="Close camera sheet"
            className="p-1 text-stone-400 hover:text-stone-700 transition-colors"
            data-testid="mobile-camera-sheet-close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {hasPhoto && (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-100">
            <Check className="h-4 w-4 text-emerald-500 flex-shrink-0" />
            <span className="text-xs font-medium text-emerald-700">
              Photo captured
            </span>
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="sr-only"
          aria-label="Arrangement photo file"
          disabled={!canUpload || busy}
          onChange={handleFileChange}
        />

        <button
          onClick={() => inputRef.current?.click()}
          disabled={!canUpload || busy}
          className={`w-full flex items-center justify-center gap-3 py-4 rounded-xl font-semibold text-base transition-colors ${
            !canUpload || busy
              ? 'bg-stone-100 text-stone-400 cursor-not-allowed'
              : hasPhoto
              ? 'bg-stone-900 text-white active:bg-stone-700'
              : 'bg-emerald-500 text-white active:bg-emerald-600'
          }`}
          data-testid="mobile-camera-trigger"
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Camera className="h-5 w-5" />
              {hasPhoto ? 'Retake Photo' : 'Take Photo'}
            </>
          )}
        </button>
        <ArrangementPhotoStatus workflow={workflow} />
        {!canUpload && !busy && !workflow.message && (
          <p className="mt-2 text-xs text-stone-500">Photo upload is unavailable. An online connection and Design permission are required.</p>
        )}
        {busy && <p className="mt-2 text-xs text-stone-500">Closing this sheet does not cancel a submitted request.</p>}
      </div>
    </>
  );
}
